import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const activeTypes = [
  "Fire sprinklers",
  "Fire alarms",
  "Portable fire extinguishers",
  "Wet & dry riser systems",
  "Smoke control systems",
  "Fire suppression systems",
];

const passiveTypes = [
  "Fire rated doors and floors",
  "Fire resistant walls",
  "Exit & emergency lights",
  "Smoke escape masks",
  "Fire blankets",
  "Fire smoke control dampers",
];

const comparisonRows = [
  {
    aspect: "How it works",
    active: "Requires activation — manual or electronic — to detect and suppress fire",
    passive: "Built into the structure; works continuously without activation",
  },
  {
    aspect: "Primary function",
    active: "Detect, suppress and ventilate during a fire emergency",
    passive: "Contain fire spread and protect evacuation routes",
  },
  {
    aspect: "Examples",
    active: "Sprinklers, alarms, extinguishers, suppression systems",
    passive: "Fire-rated doors, walls, dampers, emergency lighting",
  },
  {
    aspect: "Limitations",
    active: "May freeze in cold weather; requires power or maintenance",
    passive: "Does not actively extinguish fire; relies on structural integrity",
  },
];

export default function ActivePassiveFireProtectionPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "Difference between Active and Passive Fire Protection",
            description:
              "Understand the key differences between active and passive fire protection systems and how they work together to ensure total fire safety in Singapore.",
            publisher: { "@type": "Organization", name: "Ezzogenics" },
          }),
        }}
      />

      <main>
        {/* Hero */}
        <section className="relative pt-20 min-h-[380px] md:min-h-[460px] flex items-end">
          <div className="absolute inset-0">
            <img
              src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/bbc2ce34-313a-47cf-bb76-3ea511f8e21e_compressed_Depositphotos_2516107_l-2015.webp"
              alt="Stylized fire flames representing the threat that active and passive fire protection systems guard against"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/50 to-foreground-950/20"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 max-w-7xl mx-auto">
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-2 text-xs text-background-300 flex-wrap">
                <li><Link to="/" className="hover:text-background-50 transition-colors">Home</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to="/resources" className="hover:text-background-50 transition-colors">Resources</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-background-50 font-medium">Active vs Passive Fire Protection</li>
              </ol>
            </nav>
            <span className="inline-block text-xs font-semibold bg-primary-500 text-background-50 rounded-full px-3 py-1 mb-4">
              Fire Protection Guide
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4 max-w-3xl">
              Active vs <span className="text-primary-400">Passive Fire Protection</span>
            </h1>
            <p className="text-background-200 text-base md:text-lg max-w-2xl leading-relaxed">
              Due to the destructive nature of fire, efficient fire protection is critical to saving lives and minimizing damage. Understand the key differences to make informed decisions for your premises.
            </p>
          </div>
        </section>

        {/* Intro */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-4">
                Overview
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Why the Difference Matters
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                Active and passive fire protection systems work together, facilitating the immediate detection and response to fire emergencies. But they entail different kinds of systems and solutions that you should clearly understand in order to make informed decisions about the most suitable fire protection approach for your living and work spaces.
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                Without knowing the difference between active and passive fire protection, you could end up with systems that will not appropriately resolve the fire threats on your premises.
              </p>
              <p className="text-foreground-600 text-base leading-relaxed">
                It is recommended that residential, commercial and industrial premises are equipped with essential fire protection systems and fire safety products that cover both approaches for total fire safety.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden h-72 md:h-96">
              <img
                src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/7760756c-83f6-402c-bc6c-52476ec57411_compressed_Depositphotos_78682994_l-2015-min.webp"
                alt="Industrial concrete fire-rated roll-up shutter door for passive fire protection"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Active Fire Protection */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
              <div>
                <span className="inline-block text-xs font-semibold uppercase tracking-wider text-primary-600 bg-primary-100 rounded-full px-3 py-1 mb-4">
                  Active Systems
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  What is Active Fire Protection?
                </h2>
                <p className="text-foreground-600 text-base leading-relaxed mb-4">
                  Active fire protection refers to a series of fire protection systems that work to detect and stop fires on buildings. As the name suggests, these systems require some sort of activation either manually or electronically.
                </p>
                <p className="text-foreground-600 text-base leading-relaxed mb-4">
                  Active fire protection systems work in three main ways during fire emergencies: <strong className="text-foreground-800">detection</strong>, <strong className="text-foreground-800">suppression</strong> and <strong className="text-foreground-800">ventilation</strong>. Detection occurs through sensors that detect heat or abnormally high room temperatures, smoke and flames. When a fire occurs, these sensors detect the threats and signal the alarms to alert the occupants.
                </p>
                <p className="text-foreground-600 text-base leading-relaxed mb-6">
                  Suppression mainly refers to the actions involved in putting out fires. Active fire protection systems can extinguish fires manually or automatically depending on their functions. Ventilation emphasizes keeping escape routes safe for the evacuation of occupants during fire emergencies.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {activeTypes.map((t) => (
                    <div key={t} className="flex items-center gap-3 bg-background-50 rounded-lg px-4 py-3 border border-background-200/70">
                      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                        <i className="ri-check-line text-primary-600 text-xs"></i>
                      </div>
                      <span className="text-foreground-700 text-sm">{t}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-600 bg-accent-100 rounded-full px-3 py-1 mb-4">
                  Passive Systems
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  What is Passive Fire Protection?
                </h2>
                <p className="text-foreground-600 text-base leading-relaxed mb-4">
                  Passive fire protection also integrates different kinds of fire protection systems and fire safety products but segments the premises and uses fire rated doors and floors to prevent the spread of fire. Dividing the building into sections and installing fire resistant walls and floors also offers the occupants an ample time to evacuate the building before the fire gets bigger.
                </p>
                <p className="text-foreground-600 text-base leading-relaxed mb-4">
                  Passive fire protection also uses dampers to avoid the spread of smoke and flames through the HVAC systems. For high rise premises with multiple floors, passive fire protection systems like exit and emergency lights are recommended to ensure the safe evacuation of people during fire incidences.
                </p>
                <p className="text-foreground-600 text-base leading-relaxed mb-6">
                  Smoke escape masks, fire blankets and fire smoke control dampers are also important passive fire protection systems for ensuring fire safety in homes, offices, warehouses, schools, hospitals and other public spaces.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {passiveTypes.map((t) => (
                    <div key={t} className="flex items-center gap-3 bg-background-50 rounded-lg px-4 py-3 border border-background-200/70">
                      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                        <i className="ri-shield-check-line text-accent-600 text-xs"></i>
                      </div>
                      <span className="text-foreground-700 text-sm">{t}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
              Side-by-Side
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Active vs Passive: <span className="text-primary-500">Quick Comparison</span>
            </h2>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-background-200/70">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background-100 border-b border-background-200/70">
                  <th className="text-left px-5 py-4 font-semibold text-foreground-700 w-1/4">Aspect</th>
                  <th className="text-left px-5 py-4 font-semibold text-primary-600 w-3/8">Active Protection</th>
                  <th className="text-left px-5 py-4 font-semibold text-accent-600 w-3/8">Passive Protection</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr key={row.aspect} className={i % 2 === 0 ? "bg-background-50" : "bg-background-100/50"}>
                    <td className="px-5 py-4 font-semibold text-foreground-800">{row.aspect}</td>
                    <td className="px-5 py-4 text-foreground-600">{row.active}</td>
                    <td className="px-5 py-4 text-foreground-600">{row.passive}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Choosing Between Them */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-4">
                Making the Right Choice
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                Choosing Between Active and <span className="text-primary-500">Passive Fire Protection</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
              <div className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
                <p className="text-foreground-600 text-base leading-relaxed">
                  Active fire protection focuses on taking the necessary actions for putting out fires safely and quickly. Passive fire protection emphasizes on preventing the spread of fire and smoke. Despite their disparities, both active and passive fire protection systems are essential to the overall fire safety of a building.
                </p>
              </div>
              <div className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
                <p className="text-foreground-600 text-base leading-relaxed">
                  There are times when some active fire protection systems may not be effective. For example, fire sprinklers usually experience faults in winter as a result of freezing of the pipes. Besides, fire hydrants may also freeze in very cold environments. For better fire safety, you will need alternative passive fire protection systems to prevent the spread of the fire before finding an appropriate solution to extinguish them.
                </p>
              </div>
              <div className="bg-accent-50 rounded-2xl p-6 border border-accent-200/70 lg:col-span-2">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                    <i className="ri-lightbulb-line text-accent-600 text-lg"></i>
                  </div>
                  <p className="text-foreground-700 text-base leading-relaxed">
                    Generally, active and passive fire protection systems are essential for total fire safety in residential, commercial and industrial spaces. They both work collectively to ensure the early detection and proper response to fires, thereby saving lives and reducing the risks of property damage. The choice of the particular active and passive fire protection systems to buy for a building will mainly depend on the potential fire threats, areas to be protected, fire safety regulations and your budget.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            Need Help Choosing the Right Fire Protection Systems?
          </h2>
          <p className="text-foreground-600 text-base max-w-xl mx-auto mb-8">
            In case you are having problems choosing between active and passive fire protection, talk to Ezzogenics for assistance. We offer custom QP consultancy services for home owners, businesses and industrial establishments in Singapore.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Get QP Consultancy
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-primary-300 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap"
            >
              Browse Fire Protection Products
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}